import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Trophy, Plus, Search, Calendar, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import './RankingTournament.css';
import { CreateClassificationModal } from './CreateClassificationModal';

export const ClassificationListView = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [classifications, setClassifications] = useState<any[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
  const token = localStorage.getItem('tt_auth_token');

  const fetchClassifications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/classifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Falha ao buscar classificações');
      const data = await res.json();
      setClassifications(data);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar classificações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClassifications();
  }, []);

  const canCreate = currentUser?.role === 'ADMIN' || currentUser?.role === 'GESTOR_COMPETICAO';

  const title = 'Classificação Geral';
  const Icon = Trophy;

  const filteredClassifications = classifications
    .filter(c => c.name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(searchQuery.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()));

  const handleCreate = () => {
    setIsCreateModalOpen(true);
  };

  const handleConfirmCreate = async (data: { name: string; club: string }) => {
    try {
      const res = await fetch(`${API_URL}/api/classifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: data.name,
          clubId: data.club,
          status: 'ACTIVE'
        })
      });
      if (!res.ok) throw new Error('Falha ao criar');
      
      setIsCreateModalOpen(false);
      toast.success(`Classificação criada com sucesso!`);
      fetchClassifications();
    } catch(err) {
      console.error(err);
      toast.error('Erro ao criar classificação');
    }
  };

  const handleOpen = (id: string) => {
    navigate(`/classifications/${id}`);
  };

  return (
    <div className="rt-container">
      <div className="rt-card" style={{ padding: '32px' }}>
        
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button onClick={() => navigate(-1)} style={{ background: 'var(--bg-panel)', border: 'none', width: '48px', height: '48px', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} title="Voltar">
              <ArrowLeft size={24} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ background: 'var(--primary-color)', padding: '12px', borderRadius: '12px', display: 'flex' }}>
                <Icon size={24} color="#2D1E4B" />
              </div>
              <h1 style={{ color: 'white', fontSize: '28px', margin: 0 }}>{title}</h1>
            </div>
          </div>

          {canCreate && (
            <button 
              onClick={handleCreate}
              style={{ background: 'var(--primary-color)', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '12px', fontWeight: 700, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(181, 255, 61, 0.3)' }}
            >
              <Plus size={20} />
              Nova Classificação
            </button>
          )}
        </div>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: '32px' }}>
          <Search size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.6)' }} />
          <input 
            type="text" 
            placeholder={`Buscar classificações...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: '100%', padding: '16px 16px 16px 48px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', fontSize: '1rem', boxSizing: 'border-box' }}
          />
        </div>

        {/* List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)' }}>Carregando...</div>
            ) : filteredClassifications.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)' }}>Nenhuma classificação encontrada.</div>
            ) : (
              filteredClassifications.map(comp => (
                <motion.div 
                  key={comp.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => handleOpen(comp.id)}
                  style={{ background: 'var(--bg-panel)', padding: '24px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}
                  whileHover={{ y: -4, border: '1px solid var(--primary-color)' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ width: '64px', height: '64px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={32} color="rgba(255,255,255,0.3)" />
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <h3 style={{ margin: 0, color: 'white', fontSize: '20px' }}>{comp.name}</h3>
                        <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', background: comp.status === 'ACTIVE' ? 'rgba(111, 207, 151, 0.2)' : 'rgba(255,255,255,0.1)', color: comp.status === 'ACTIVE' ? '#6FCF97' : 'rgba(255,255,255,0.6)' }}>
                          {comp.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '24px', color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                         <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Calendar size={16} /> Criado em: {new Date(comp.createdAt).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</span>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <ChevronRight color="var(--primary-color)" />
                  </div>
                </motion.div>
              ))
            )}
        </div>
      </div>

      <CreateClassificationModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleConfirmCreate}
      />
    </div>
  );
};
